import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    urlParamReplicaId,
    urlParamUniversalId,
    urlProjectDocumentActivityLogs,
    urlProjectDocumentActivityLogsConfig,
    urlProjectDocumentMetadata,
} from '../../Constants';
import { IDocDefaultFields } from './documents/sliceProjectDocuments';
import { IAction, SlicePropsWithData } from '../store';
import { IActivityLogActivity } from '../../components/General/Sidepanels/Components/DocumentActivityLogs';
import { IActivityLogAssigment } from '../../components/General/Sidepanels/Components/AssignmentActivityLogs';
import { IActivityLogWorkflow } from '../../components/General/Sidepanels/Components/WorkflowActivityLogs';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Enum that describes the different panels that are available in the Project Sidepanel.
 */
export enum ProjectSidepanel {
    None,
    General,
    Files,
    Documents,
    Activities,
    History,
    Permissions,
}

/**
 * Initial slice state value.
 */
const initialState: IProjectSidepanelState = {
    active: ProjectSidepanel.None,
    data: {},
    loading: false,
    error: null,
    targetDocId: null,
    targetNoteId: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    /**
     * Updates the active visible sidepanel.
     * @param state - Current slice state object.
     * @param action - Action containing the new visible sidepanel.
     */
    setActiveSidepanel(state: IProjectSidepanelState, action: IAction<ProjectSidepanel>) {
        state.active = action.payload;

        if (action.payload === ProjectSidepanel.None) {
            state.targetDocId = null;
            state.targetNoteId = null;
        }
    },

    /**
     * Updates the target document.
     * @param state - Current slice state object.
     * @param action - Action containing the target document's IdDocIndex.
     */
    setActiveDocument(
        state: IProjectSidepanelState,
        action: IAction<{ UniversalId: string; NoteId: string }>,
    ) {
        const { UniversalId, NoteId } = action.payload;

        state.data = {};

        state.targetDocId = UniversalId;
        state.targetNoteId = NoteId;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches a dynamic subset of fields for a given document in a project database.
     * @param config - Object that contains the project database's replicaId, the active view, and the idDocIndex of the docuement to load data for.
     * @returns Promise for the HTTP request.
     */
    getDocumentMetadata: async (config: {
        replicaId: string;
        viewName: string;
        universalId: string;
        fields: string[];
        tab: ProjectSidepanel;
    }): Promise<ProjectSidepanelFieldData> => {
        try {
            const { universalId, replicaId, viewName, fields, tab } = config;

            const request: RequestInit = {
                headers: {
                    viewName,
                    fieldset: fields.join(';'),
                },
            };

            const url = urlProjectDocumentMetadata
                .replace(urlParamReplicaId, replicaId)
                .replace(urlParamUniversalId, universalId);

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve({ [tab]: await response.json() })
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    getDocumentActivityLogs: async (config: {
        replicaId: string;
        universalId: string;
    }): Promise<IActivityLogs> => {
        try {
            const { universalId, replicaId } = config;

            const request: RequestInit = {
                headers: {
                    replicaId,
                    universalId,
                },
            };

            const response = await apiFetch(urlProjectDocumentActivityLogs, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    getDocumentActivityLogsConfig: async (): Promise<IActivityLogConfig[]> => {
        try {
            const response = await apiFetch(urlProjectDocumentActivityLogsConfig);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectSidepanelState>) => {
        /**
         * Reducers for the 'getDocumentMetadata' thunk.
         */

        builder.addCase(getDocumentMetadata.pending, (state: Draft<IProjectSidepanelState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getDocumentMetadata.fulfilled,
            (state: Draft<IProjectSidepanelState>, action: IAction<ProjectSidepanelFieldData>) => {
                state.loading = false;
                state.error = null;
                state.data = { ...state.data, ...action.payload };
            },
        );
        builder.addCase(
            getDocumentMetadata.rejected,
            (state: Draft<IProjectSidepanelState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );

        builder.addCase(getDocumentActivityLogs.pending, (state: Draft<IProjectSidepanelState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getDocumentActivityLogs.fulfilled,
            (state: Draft<IProjectSidepanelState>, action) => {
                state.loading = false;
                state.data = {
                    ...state.data,
                    [ProjectSidepanel.Activities.toString()]: {
                        ...state.data?.[ProjectSidepanel.Activities.toString()],
                        logs: action.payload,
                    },
                };
                state.error = null;
            },
        );
        builder.addCase(
            getDocumentActivityLogs.rejected,
            (state: Draft<IProjectSidepanelState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.data = null;
                state.error = action.error;
            },
        );

        builder.addCase(
            getDocumentActivityLogsConfig.pending,
            (state: Draft<IProjectSidepanelState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getDocumentActivityLogsConfig.fulfilled,
            (state: Draft<IProjectSidepanelState>, action) => {
                state.loading = false;
                state.data = {
                    ...state.data,
                    [ProjectSidepanel.Activities.toString()]: {
                        ...state.data?.[ProjectSidepanel.Activities.toString()],
                        config: action.payload,
                    },
                };
                state.error = null;
            },
        );
        builder.addCase(
            getDocumentActivityLogsConfig.rejected,
            (state: Draft<IProjectSidepanelState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.data = null;
                state.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getDocumentMetadata = createAsyncThunk(
    'project/docMetadata',
    thunks.getDocumentMetadata,
);

export const getDocumentActivityLogs = createAsyncThunk(
    urlProjectDocumentActivityLogs,
    thunks.getDocumentActivityLogs,
);

export const getDocumentActivityLogsConfig = createAsyncThunk(
    urlProjectDocumentActivityLogsConfig,
    thunks.getDocumentActivityLogsConfig,
);

/** Dictionary that contains the field data for each sidepanel. */
type ProjectSidepanelFieldData = { [K in ProjectSidepanel]?: IDocDefaultFields };

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectSidepanelState extends SlicePropsWithData<ProjectSidepanelFieldData> {
    /** Active open sidepanel. */
    active: ProjectSidepanel;

    /** UniversalId of the document that is open in the sidepanel. */
    targetDocId: string;

    /** NoteId of the document that is open in the sidepanel. */
    targetNoteId: string;
}

/**
 * Interface that describes a signature entry for a document.
 */
export interface ISignatureData {
    /** Date of the signature. */
    date: number;

    /** Signature status Id. */
    status: string;

    /** The Workflow Task the document was in when it was signed. */
    task: string;

    /** User who signed the document. */
    user: string;
}

export interface IActivityLogs {
    activity: IActivityLogActivity[];
    assignments: IActivityLogAssigment[];
    workflow: IActivityLogWorkflow[];
}

export interface IActivityLogConfig {
    Name: string;
    Color: string;
    Icon: string;
    Translations: { [id: string]: string };
}

/**
 * Create the redux slice.
 */
export const projectSidepanelSlice = createSlice({
    name: 'projectSidepanel',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { setActiveDocument, setActiveSidepanel } = projectSidepanelSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectSidepanelSlice.reducer;
